import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, CheckCircle, ChevronRight, Phone, MapPin, Zap, Shield, Clock, Award, Briefcase } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

const slugify = (str: string) => str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
import { useSiteContent } from '@/context/SiteContentContext';
import { useSectionVisibility, SectionVisibility } from '@/hooks/useSectionVisibility';
import { useContentMode } from '@/hooks/useContentMode';
import { useCmsTheme } from '@/context/CmsThemeContext';
import { usePageDesign } from '@/hooks/usePageDesign';
import { pmsFrom } from '@/integrations/pms';
import { Button } from '@/components/ui/button';
import DynamicIcon from '@/components/common/DynamicIcon';
import { LeadForm } from '@/components/forms/LeadForm';
import { useAnimateOnView } from '@/hooks/useAnimateOnView';
import { useCountUp } from '@/hooks/useCountUp';
import { cn } from '@/lib/utils';
import { CmsThemedHero } from '@/components/cms-theme/CmsThemedHero';
import { CustomHero } from '@/components/custom-theme/CustomHero';

// ── Layout context — lets every section know which layout is active ──
type LayoutId = 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5';
const LayoutCtx = React.createContext<LayoutId>('layout_1');
const useLayout = () => React.useContext(LayoutCtx);

// ── Animation helpers ──
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const stagger = (i: number) => ({ ...fadeUp, transition: { duration: 0.5, delay: i * 0.1 } });

// ── Stat Counter ──
const StatCounter: React.FC<{ value: number; suffix?: string; label: string; delay?: number }> = ({
  value, suffix = '', label, delay = 0,
}) => {
  const { ref, formattedCount } = useCountUp({ end: value, suffix, delay, duration: 2000 });
  return (
    <div ref={ref}>
      <div className="text-4xl lg:text-5xl font-heading font-bold tracking-tight text-foreground">{formattedCount}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
    </div>
  );
};

// ── Section Badge ──
const SectionBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest text-muted-foreground border border-border mb-6">
    {children}
  </span>
);

// ══════════════════════════════════════════════════════════════════
// HERO — Full-width, left-aligned, dramatic
// ══════════════════════════════════════════════════════════════════
const HeroSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {

  return (
    <section className="relative min-h-[100vh] flex items-center bg-background overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
        backgroundSize: '80px 80px',
      }} />

      <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-32 pb-20">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          {/* Left — Copy */}
          <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            {/* Trust badges row */}
            <div className="flex flex-wrap gap-3 mb-8">
              {ctx.heroBadges.map((badge, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 border border-border rounded-full text-xs font-medium text-muted-foreground">
                  <CheckCircle className="h-3.5 w-3.5" />{badge}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-[1.05] tracking-tight mb-6">
              {ctx.heroHeadline}
            </h1>

            <p className="text-lg lg:text-xl text-muted-foreground mb-10 max-w-xl leading-relaxed">
              {ctx.heroSubheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Link to="/contact" className="btn-cta text-base px-8 py-4">
                {ctx.heroCTAPrimary}
                <ArrowRight size={18} />
              </Link>
              <a href={`tel:${ctx.template.contact?.phone?.replace(/\D/g, '') || ''}`} className="btn-secondary text-base px-8 py-4">
                <Phone size={18} />
                {ctx.heroCTASecondary}
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-border">
              <StatCounter value={ctx.template.stats?.yearsInBusiness || 10} suffix="+" label="Years Experience" />
              <StatCounter value={ctx.template.stats?.projectsCompleted || 500} suffix="+" label="Projects Done" delay={200} />
              <StatCounter value={ctx.template.stats?.googleRating || 4.9} label="Google Rating" delay={400} />
            </div>
          </motion.div>

          {/* Right — Lead Form */}
          <motion.div className="lg:col-span-5 hidden lg:block" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <div className="bg-foreground text-background rounded-2xl p-8 shadow-2xl">
              <LeadForm title="Get Your Free Quote" subtitle="Fill out the form and we'll respond within 24 hours." showPromoField source="homepage_hero" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// HERO LAYOUT 2 — "Bold Showcase"
// Full-bleed dark immersive hero. Giant animated counter as visual
// anchor, horizontal services ticker, glassmorphism lead form card.
// Design language: Apple keynote meets premium contractor.
// ══════════════════════════════════════════════════════════════════
const HeroLayout2: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const years = ctx.template.stats?.yearsInBusiness || 10;
  const projects = ctx.template.stats?.projectsCompleted || 500;
  const rating = ctx.template.stats?.googleRating || 4.9;
  const phone = ctx.template.contact?.phone?.replace(/\D/g, '') || '';

  // Fetch services for the ticker
  const { data: tickerServices = [] } = useQuery({
    queryKey: ['services-hero-ticker'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('services')
        .select('name, icon')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(20);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const serviceNames = tickerServices.length > 0
    ? tickerServices.map(s => s.name)
    : ctx.services.map(s => s.title);

  return (
    <section className="relative min-h-[100vh] bg-foreground text-background overflow-hidden flex flex-col">
      {/* Layered background */}
      <div className="absolute inset-0">
        {/* Noise grain */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        }} />
        {/* Accent orb top-right */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-primary/20 blur-[120px]" />
        {/* Accent orb bottom-left */}
        <div className="absolute -bottom-48 -left-48 w-[400px] h-[400px] rounded-full bg-primary/10 blur-[100px]" />
      </div>

      {/* ── Top section: headline + stats ── */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="container mx-auto px-4 lg:px-8 py-28 lg:py-36">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">

            {/* Left column — giant stat + copy */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Giant animated number */}
              <div className="mb-8 flex items-end gap-4">
                <span className="text-[120px] md:text-[160px] lg:text-[200px] font-heading font-black leading-none tracking-tighter bg-gradient-to-b from-background to-background/30 bg-clip-text text-transparent">
                  {years}
                </span>
                <span className="text-lg md:text-xl font-medium text-background/40 pb-6 leading-tight">
                  years of<br />trusted service
                </span>
              </div>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6 max-w-2xl">
                {ctx.heroHeadline}
              </h1>

              <p className="text-lg text-background/50 mb-10 max-w-lg leading-relaxed">
                {ctx.heroSubheadline}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-background text-foreground text-base font-semibold hover:bg-background/90 transition-all shadow-lg shadow-background/20">
                  {ctx.heroCTAPrimary} <ArrowRight size={18} />
                </Link>
                <a href={`tel:${phone}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-background/15 text-background/80 text-base font-medium hover:bg-background/5 transition-all backdrop-blur-sm">
                  <Phone size={18} /> {ctx.heroCTASecondary}
                </a>
              </div>
            </motion.div>

            {/* Right column — glass lead form */}
            <motion.div
              className="lg:col-span-5 hidden lg:block"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="relative">
                {/* Glow behind card */}
                <div className="absolute -inset-4 rounded-3xl bg-primary/10 blur-2xl" />
                <div className="relative bg-background/[0.06] backdrop-blur-xl border border-background/10 rounded-2xl p-8">
                  <LeadForm title="Get Your Free Quote" subtitle="We respond within 24 hours." showPromoField source="homepage_hero_l2" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── Services ticker ── */}
      {serviceNames.length > 0 && (
        <div className="relative z-10 border-t border-background/8">
          <div className="overflow-hidden py-5">
            <motion.div
              className="flex gap-8 whitespace-nowrap"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            >
              {[...serviceNames, ...serviceNames].map((name, i) => (
                <span key={i} className="text-sm font-medium text-background/30 uppercase tracking-widest flex items-center gap-3 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                  {name}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      )}

      {/* ── Bottom stats bar ── */}
      <div className="relative z-10 border-t border-background/8">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-background/8">
            {[
              { value: `${years}+`, label: 'Years in Business', icon: <Clock className="h-4 w-4" /> },
              { value: `${projects}+`, label: 'Projects Completed', icon: <Briefcase className="h-4 w-4" /> },
              { value: `${rating}★`, label: 'Google Rating', icon: <Star className="h-4 w-4" /> },
              { value: '24/7', label: 'Emergency Service', icon: <Zap className="h-4 w-4" /> },
            ].map((stat, i) => (
              <motion.div
                key={i}
                className="py-6 lg:py-8 px-6 flex items-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
              >
                <div className="text-primary/60">{stat.icon}</div>
                <div>
                  <div className="text-xl lg:text-2xl font-heading font-bold">{stat.value}</div>
                  <div className="text-xs text-background/30 mt-0.5">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// HERO LAYOUT 3 — "Premium Split"
// Luxury-contractor feel. Two-tone split: primary-tinted left with
// headline + stats, white right with lead form. Generous whitespace,
// oversized typography, monospaced section labels throughout.
// ══════════════════════════════════════════════════════════════════
const HeroLayout3: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const years = ctx.template.stats?.yearsInBusiness || 10;
  const projects = ctx.template.stats?.projectsCompleted || 500;
  const rating = ctx.template.stats?.googleRating || 4.9;
  const phone = ctx.template.contact?.phone || '(555) 234-5678';
  const phoneTel = phone.replace(/\D/g, '');

  return (
    <section className="relative min-h-[100vh] bg-background overflow-hidden">
      <div className="grid lg:grid-cols-2 min-h-[100vh]">
        {/* ── Left: Primary accent panel ── */}
        <div className="relative bg-foreground text-background flex flex-col justify-center px-8 lg:px-16 xl:px-24 py-28 lg:py-20">
          {/* Subtle texture */}
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: 'radial-gradient(circle, hsl(var(--primary) / 0.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }} />
          <div className="relative z-10 max-w-xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-1 mb-8">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} className="h-4 w-4 text-amber-400 fill-amber-400" />
                ))}
                <span className="text-sm text-background/50 ml-2">{rating} · Google Reviews</span>
              </div>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight mb-6">
                {ctx.heroHeadline}
              </h1>

              <p className="text-lg text-background/50 mb-10 leading-relaxed">
                {ctx.heroSubheadline}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-16">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-background text-foreground text-base font-semibold hover:bg-background/90 transition-all">
                  {ctx.heroCTAPrimary} <ArrowRight size={18} />
                </Link>
                <a href={`tel:${phoneTel}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-background/15 text-background/80 text-base font-medium hover:bg-background/5 transition-all">
                  <Phone size={18} /> {ctx.heroCTASecondary}
                </a>
              </div>

              {/* Stat row */}
              <div className="grid grid-cols-3 gap-8 pt-8 border-t border-background/10">
                <div><div className="text-3xl font-heading font-bold"><StatCounter value={years} suffix="+" label="" /></div><div className="text-xs text-background/30 mt-1 uppercase tracking-wider">Years</div></div>
                <div><div className="text-3xl font-heading font-bold"><StatCounter value={projects} suffix="+" label="" delay={200} /></div><div className="text-xs text-background/30 mt-1 uppercase tracking-wider">Projects</div></div>
                <div><div className="text-3xl font-heading font-bold"><StatCounter value={rating} suffix="★" label="" delay={400} /></div><div className="text-xs text-background/30 mt-1 uppercase tracking-wider">Rating</div></div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── Right: Form panel ── */}
        <div className="flex flex-col justify-center px-8 lg:px-16 xl:px-24 py-20 lg:py-16 bg-background">
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="max-w-md mx-auto w-full">
            {/* Trust row */}
            <div className="flex flex-wrap gap-3 mb-8">
              {ctx.heroBadges.map((badge, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-full text-xs font-medium text-muted-foreground">
                  <CheckCircle className="h-3.5 w-3.5 text-primary" />{badge}
                </span>
              ))}
            </div>
            <LeadForm title="Get Your Free Estimate" subtitle="We respond within the hour — no obligation." showPromoField source="homepage_hero_l3" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// HERO LAYOUT 4 — "Cinematic"
// Full-viewport immersive hero with gradient overlay, floating glass
// content card, and a horizontal scrolling services ribbon. Feels
// like a blockbuster movie landing page for contractors.
// ══════════════════════════════════════════════════════════════════
const HeroLayout4: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const years = ctx.template.stats?.yearsInBusiness || 10;
  const projects = ctx.template.stats?.projectsCompleted || 500;
  const rating = ctx.template.stats?.googleRating || 4.9;
  const phone = ctx.template.contact?.phone || '(555) 234-5678';
  const phoneTel = phone.replace(/\D/g, '');

  const { data: tickerServices = [] } = useQuery({
    queryKey: ['services-hero-ticker'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('services')
        .select('name, icon')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(20);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });
  const serviceNames = tickerServices.length > 0
    ? tickerServices.map(s => s.name)
    : ctx.services.map(s => s.title);

  return (
    <section className="relative min-h-[100vh] bg-foreground text-background overflow-hidden flex flex-col">
      {/* Cinematic gradient layers */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-foreground to-foreground" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-transparent to-transparent" />
        <div className="absolute top-0 right-0 w-[60%] h-full bg-gradient-to-l from-primary/10 to-transparent" />
        {/* Animated grain */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="container mx-auto px-4 lg:px-8 py-32 lg:py-40">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left — Cinematic copy */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              {/* Accent line + label */}
              <div className="flex items-center gap-4 mb-10">
                <div className="w-12 h-px bg-primary" />
                <span className="text-xs uppercase tracking-[0.3em] text-background/40 font-medium">Est. {new Date().getFullYear() - years}</span>
              </div>

              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] tracking-tight mb-8">
                {ctx.heroHeadline}
              </h1>

              <p className="text-xl lg:text-2xl text-background/40 mb-12 max-w-xl leading-relaxed font-light">
                {ctx.heroSubheadline}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-16">
                <Link to="/contact" className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-primary text-primary-foreground rounded-full text-base font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/25">
                  {ctx.heroCTAPrimary} <ArrowRight size={18} />
                </Link>
                <a href={`tel:${phoneTel}`} className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full border border-background/15 text-background/80 text-base font-medium hover:bg-background/5 transition-all backdrop-blur-sm">
                  <Phone size={18} /> {ctx.heroCTASecondary}
                </a>
              </div>

              {/* Inline stats */}
              <div className="flex items-center gap-12">
                {[
                  { val: `${years}+`, label: 'Years' },
                  { val: `${projects}+`, label: 'Projects' },
                  { val: `${rating}`, label: 'Rating' },
                ].map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.15, duration: 0.5 }}>
                    <div className="text-3xl font-heading font-bold">{s.val}</div>
                    <div className="text-xs text-background/25 uppercase tracking-wider mt-1">{s.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right — Floating glass form */}
            <motion.div
              className="lg:col-span-5 hidden lg:block"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative">
                <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-primary/30 to-primary/5 blur-2xl" />
                <div className="relative bg-background/[0.08] backdrop-blur-2xl border border-background/10 rounded-3xl p-10 shadow-2xl">
                  <div className="flex items-center gap-2 mb-6">
                    {ctx.heroBadges.slice(0, 2).map((badge, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium text-background/50 border border-background/10">
                        <CheckCircle className="h-3 w-3 text-primary/70" />{badge}
                      </span>
                    ))}
                  </div>
                  <LeadForm title="Free Estimate" subtitle="We respond within the hour." showPromoField source="homepage_hero_l4" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Service ticker */}
      {serviceNames.length > 0 && (
        <div className="relative z-10 border-t border-background/6">
          <div className="overflow-hidden py-5">
            <motion.div
              className="flex gap-10 whitespace-nowrap"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
            >
              {[...serviceNames, ...serviceNames].map((name, i) => (
                <span key={i} className="text-[13px] font-medium text-background/20 uppercase tracking-widest flex items-center gap-3 shrink-0">
                  <span className="w-1 h-1 rounded-full bg-primary/50" />
                  {name}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      )}
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// HERO LAYOUT 5 — "Architect"
// Extreme minimalism: oversized single-line headline, massive
// whitespace, accent-color decorative blocks, monospaced metadata.
// Feels like a high-end architecture/design studio.
// ══════════════════════════════════════════════════════════════════
const HeroLayout5: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const years = ctx.template.stats?.yearsInBusiness || 10;
  const projects = ctx.template.stats?.projectsCompleted || 500;
  const rating = ctx.template.stats?.googleRating || 4.9;
  const phone = ctx.template.contact?.phone || '(555) 234-5678';
  const phoneTel = phone.replace(/\D/g, '');

  // Split headline into words for staggered animation
  const headlineWords = ctx.heroHeadline.split(' ');

  return (
    <section className="relative min-h-[100vh] bg-background overflow-hidden flex flex-col">
      {/* Decorative accent block — top right */}
      <div className="absolute top-0 right-0 w-[35%] h-[45%] bg-primary/[0.04]" />
      {/* Thin accent line */}
      <div className="absolute top-0 left-[15%] w-px h-full bg-border/50" />

      <div className="relative z-10 flex-1 flex items-center">
        <div className="container mx-auto px-4 lg:px-8 py-32 lg:py-24">
          {/* Top metadata row */}
          <motion.div
            className="flex items-center justify-between mb-20 lg:mb-28"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-8">
              <span className="text-[11px] font-mono text-muted-foreground">{new Date().getFullYear() - years}—{new Date().getFullYear()}</span>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} className="h-3 w-3 text-foreground fill-foreground" />
                ))}
                <span className="text-[11px] font-mono text-muted-foreground ml-1.5">{rating}</span>
              </div>
            </div>
            <a href={`tel:${phoneTel}`} className="text-[11px] font-mono text-muted-foreground hover:text-foreground transition-colors">
              {phone}
            </a>
          </motion.div>

          {/* Giant headline — staggered word animation */}
          <div className="mb-16 lg:mb-20 max-w-6xl">
            <div className="flex flex-wrap gap-x-[0.35em]">
              {headlineWords.map((word, i) => (
                <motion.span
                  key={i}
                  className="font-heading text-6xl md:text-7xl lg:text-8xl xl:text-[9rem] font-bold tracking-tight leading-[0.9]"
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.08 }}
                >
                  {word}
                </motion.span>
              ))}
            </div>
          </div>

          {/* Sub content: description + CTAs + form side-by-side */}
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="w-12 h-1 bg-primary mb-8" />
              <p className="text-lg text-muted-foreground leading-relaxed mb-10">
                {ctx.heroSubheadline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-foreground text-background rounded-none text-sm font-semibold uppercase tracking-wider hover:bg-foreground/90 transition-all">
                  {ctx.heroCTAPrimary} <ArrowRight size={16} />
                </Link>
                <a href={`tel:${phoneTel}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-foreground text-foreground rounded-none text-sm font-semibold uppercase tracking-wider hover:bg-foreground hover:text-background transition-all">
                  <Phone size={16} /> Call Now
                </a>
              </div>

              {/* Stats — horizontal */}
              <div className="flex gap-10 pt-8 border-t border-border">
                {[
                  { val: years, suffix: '+', label: 'Years' },
                  { val: projects, suffix: '+', label: 'Projects' },
                  { val: rating, suffix: '', label: 'Rating' },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-2xl font-heading font-bold tracking-tight">
                      <StatCounter value={s.val} suffix={s.suffix} label="" delay={i * 200} />
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-5 lg:col-start-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <div className="border-2 border-foreground p-8 lg:p-10">
                <div className="flex flex-wrap gap-2 mb-6">
                  {ctx.heroBadges.map((badge, i) => (
                    <span key={i} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{badge}</span>
                  ))}
                </div>
                <LeadForm title="Request a Quote" subtitle="Free estimate — no obligation." showPromoField source="homepage_hero_l5" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// SEGMENTS — Customer types
// ══════════════════════════════════════════════════════════════════
const SegmentsSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const layout = useLayout();
  const { data: segments = [] } = useQuery({
    queryKey: ['service-segments-public'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('service_segments')
        .select('id, name, slug, icon, description, is_active')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  if (!segments.length) return null;

  // ── Layout 2: dark background, pill-style segment tags ──
  if (layout === 'layout_2') return (
    <section className="py-20 lg:py-28 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <p className="text-xs uppercase tracking-widest text-background/60 font-semibold mb-8">Who We Serve</p>
        <div className="flex flex-wrap gap-3">
          {segments.map((segment, i) => (
            <motion.div key={segment.id} {...stagger(i)}>
              <Link to={`/services/${segment.slug}`} className="group inline-flex items-center gap-3 px-6 py-4 rounded-full border border-background/10 hover:border-background/25 hover:bg-background/5 transition-all">
                <DynamicIcon name={segment.icon || 'Users'} className="h-5 w-5 text-primary/70" />
                <span className="font-medium">{segment.name}</span>
                <ArrowRight className="h-4 w-4 text-background/20 group-hover:text-background/50 group-hover:translate-x-1 transition-all" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Layout 3: alternating side cards ──
  if (layout === 'layout_3') return (
    <section className="py-24 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4 block">01 — Who We Serve</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">Built for every customer</h2>
            <p className="text-muted-foreground leading-relaxed">Specialized solutions for each segment we serve, backed by dedicated teams and industry expertise.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {segments.map((segment, i) => (
              <motion.div key={segment.id} {...stagger(i)}>
                <Link to={`/services/${segment.slug}`} className="group block p-6 bg-background border border-border rounded-xl hover:border-primary/30 hover:shadow-lg transition-all">
                  <DynamicIcon name={segment.icon || 'Users'} className="h-8 w-8 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-1">{segment.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-2">{segment.description || 'Specialized services.'}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 4: cinematic horizontal scroll cards ──
  if (layout === 'layout_4') return (
    <section className="py-20 lg:py-28 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="flex items-center gap-4 mb-4"><div className="w-8 h-px bg-primary" /><span className="text-xs uppercase tracking-[0.3em] text-background/30">Segments</span></div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">Who we serve</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {segments.map((segment, i) => (
            <motion.div key={segment.id} {...stagger(i)}>
              <Link to={`/services/${segment.slug}`} className="group block p-8 rounded-2xl bg-background/[0.06] border border-background/8 hover:bg-background/10 hover:border-background/15 transition-all duration-300">
                <DynamicIcon name={segment.icon || 'Users'} className="h-10 w-10 text-primary/80 mb-6" />
                <h3 className="font-heading font-semibold text-lg mb-2">{segment.name}</h3>
                <p className="text-sm text-background/35 leading-relaxed line-clamp-2 mb-4">{segment.description || 'Specialized services.'}</p>
                <span className="text-xs text-primary/70 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">Explore <ArrowRight className="h-3.5 w-3.5" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Layout 5: minimal offset cards ──
  if (layout === 'layout_5') return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-3">Segments</span>
            <div className="w-8 h-0.5 bg-primary mb-6" />
            <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mb-3">Who we serve</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">Dedicated solutions for every customer type.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-border">
            {segments.map((segment, i) => (
              <motion.div key={segment.id} {...stagger(i)}>
                <Link to={`/services/${segment.slug}`} className="group block p-8 bg-background hover:bg-muted/50 transition-colors">
                  <DynamicIcon name={segment.icon || 'Users'} className="h-6 w-6 text-foreground/40 mb-4 group-hover:text-primary transition-colors" />
                  <h3 className="font-heading font-semibold text-base mb-1">{segment.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-2">{segment.description || 'Specialized services.'}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 1: original ──
  return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <SectionBadge>Who We Serve</SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Built for every customer</h2>
          <p className="text-muted-foreground text-lg">Specialized solutions for each segment we serve.</p>
        </div>
        <div className={cn('grid gap-6', segments.length <= 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : segments.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4')}>
          {segments.map((segment, i) => (
            <motion.div key={segment.id} {...stagger(i)}>
              <Link to={`/services/${segment.slug}`} className="group block p-8 border border-border rounded-xl text-center transition-all duration-300 hover:border-foreground/20 hover:shadow-lg">
                <div className="w-14 h-14 rounded-full border-2 border-foreground/10 flex items-center justify-center mx-auto mb-5 group-hover:border-foreground/30 transition-colors">
                  <DynamicIcon name={segment.icon || 'Users'} className="h-6 w-6 text-foreground/60 group-hover:text-foreground transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{segment.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{segment.description || 'Specialized services.'}</p>
                <span className="inline-flex items-center text-sm font-medium text-foreground group-hover:gap-2 transition-all">View Services <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// SERVICES — Clean grid
// ══════════════════════════════════════════════════════════════════
const ServicesSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  // Query the services DB table — this is what the admin Services Management panel manages
  const { data: dbServices = [] } = useQuery({
    queryKey: ['services-homepage'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('services')
        .select('id, name, description, icon, slug, is_active, is_featured, sort_order, service_categories!inner(slug, service_segments!inner(slug))')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(12);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Prefer DB services, fall back to ctx.services (site_content JSON / template defaults)
  const services = dbServices.length > 0
    ? dbServices.map(s => {
        const cat = (s as any).service_categories;
        const seg = cat?.service_segments;
        return {
          title: s.name,
          description: s.description || '',
          icon: s.icon || 'Wrench',
          slug: s.slug,
          // Build proper deep link: /segment/category/service
          href: seg && cat ? `/${seg.slug}/${cat.slug}/${s.slug}` : `/services/${s.slug}`,
        };
      })
    : ctx.services.map(s => ({
        title: s.title,
        description: s.description,
        icon: s.icon,
        slug: s.slug,
        href: `/services/${s.slug}`,
      }));

  const layout = useLayout();
  if (services.length === 0) return null;

  // ── Layout 2: bold numbered list on dark bg ──
  if (layout === 'layout_2') return (
    <section className="py-20 lg:py-28 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Services</p><h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">What we do</h2></div>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="space-y-0 divide-y divide-border border-t border-b border-border">
          {services.slice(0, 8).map((service, i) => (
            <motion.div key={service.slug} {...stagger(i)}>
              <Link to={service.href} className="group flex items-center gap-6 py-6 hover:bg-muted/30 px-2 -mx-2 rounded-lg transition-colors">
                <span className="text-3xl font-heading font-bold text-muted-foreground/20 w-12 text-right shrink-0">{String(i + 1).padStart(2, '0')}</span>
                <DynamicIcon name={service.icon} className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-lg group-hover:text-primary transition-colors">{service.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-1">{service.description}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground/30 group-hover:text-foreground group-hover:translate-x-1 transition-all shrink-0" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Layout 3: premium grid with large icons and accent borders ──
  if (layout === 'layout_3') return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4 block">02 — Services</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">What we do</h2>
          </div>
          <Link to="/services" className="text-sm font-medium text-primary hover:underline flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 6).map((service, i) => (
            <motion.div key={service.slug} {...stagger(i)}>
              <Link to={service.href} className="group block p-8 bg-background border border-border rounded-2xl hover:border-primary/40 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary/0 group-hover:bg-primary transition-all duration-300" />
                <div className="w-12 h-12 rounded-xl bg-foreground text-background flex items-center justify-center mb-5 group-hover:bg-primary transition-colors">
                  <DynamicIcon name={service.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">{service.description}</p>
                <span className="inline-flex items-center text-xs font-semibold text-primary group-hover:gap-2 transition-all">Learn more <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Layout 4: cinematic dark cards with glow ──
  if (layout === 'layout_4') return (
    <section className="py-24 lg:py-32 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="flex items-center gap-4 mb-4"><div className="w-8 h-px bg-primary" /><span className="text-xs uppercase tracking-[0.3em] text-background/30">Services</span></div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">What we do</h2>
          </div>
          <Link to="/services" className="hidden lg:flex items-center gap-2 text-sm text-background/40 hover:text-background transition-colors">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.slice(0, 6).map((service, i) => (
            <motion.div key={service.slug} {...stagger(i)}>
              <Link to={service.href} className="group block p-8 rounded-2xl bg-background/[0.04] border border-background/6 hover:bg-background/8 hover:border-primary/20 transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary/5 to-transparent" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <DynamicIcon name={service.icon} className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{service.title}</h3>
                  <p className="text-sm text-background/35 leading-relaxed line-clamp-3 mb-5">{service.description}</p>
                  <span className="inline-flex items-center text-xs font-medium text-primary/80 group-hover:text-primary group-hover:gap-2 transition-all">Learn more <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10 lg:hidden"><Link to="/services" className="text-sm text-background/40 hover:text-background">View all services <ArrowRight className="h-4 w-4 inline ml-1" /></Link></div>
      </div>
    </section>
  );

  // ── Layout 5: brutalist list ──
  if (layout === 'layout_5') return (
    <section className="py-24 lg:py-32 border-t-2 border-foreground">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-3">Services</span>
            <div className="w-8 h-0.5 bg-primary mb-6" />
            <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mb-3">What we do</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">Professional solutions for every project.</p>
            <Link to="/services" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground border-b-2 border-foreground pb-1 hover:text-primary hover:border-primary transition-colors">View all <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="lg:col-span-8 space-y-0 divide-y-2 divide-foreground/10 border-t-2 border-foreground/10">
            {services.slice(0, 6).map((service, i) => (
              <motion.div key={service.slug} {...stagger(i)}>
                <Link to={service.href} className="group flex items-center gap-6 py-7 hover:pl-4 transition-all">
                  <span className="text-4xl font-heading font-black text-foreground/[0.06] w-14 text-right shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-lg group-hover:text-primary transition-colors">{service.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{service.description}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-foreground/10 group-hover:text-primary group-hover:translate-x-2 transition-all shrink-0" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 1: original grid ──
  return (
    <section className="py-24 lg:py-32 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <SectionBadge>Services</SectionBadge>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">What we do</h2>
            <p className="text-muted-foreground text-lg">Professional solutions backed by years of expertise.</p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">View all services <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden">
          {services.slice(0, 6).map((service, i) => (
            <motion.div key={service.slug} {...stagger(i)}>
              <Link to={service.href} className="group block p-8 bg-background hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0 group-hover:bg-foreground group-hover:text-background transition-all">
                    <DynamicIcon name={service.icon} className="h-5 w-5 text-foreground/50 group-hover:text-background transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-base mb-1.5">{service.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{service.description}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border/50"><span className="inline-flex items-center text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">Learn more <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-1 transition-transform" /></span></div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// WHY CHOOSE US
// ══════════════════════════════════════════════════════════════════
const WhyChooseUsSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const layout = useLayout();
  const { data: trustBadges = [] } = useQuery({
    queryKey: ['trust-badges-homepage'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('business_settings').select('trust_badges').limit(1).maybeSingle();
      if (error) throw error;
      return (data?.trust_badges as Array<{ icon: string; label: string }>) || [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const hasTrustBadges = trustBadges.length > 0;
  const hasDifferentiators = ctx.differentiators.length > 0;
  if (!hasTrustBadges && !hasDifferentiators) return null;

  // ── Layout 2: inline icon rows on dark bg ──
  if (layout === 'layout_2') return (
    <section className="py-20 lg:py-28 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-xs uppercase tracking-widest text-background/60 font-semibold mb-3">Why Us</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Built different</h2>
            <p className="text-background/50 text-lg">We don't just meet expectations — we redefine them.</p>
          </div>
          <div className="space-y-6">
            {hasTrustBadges && (
              <div className="flex flex-wrap gap-3 mb-4">
                {trustBadges.map((badge, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-background/10 text-sm text-background/60">
                    <DynamicIcon name={badge.icon || 'Shield'} className="h-4 w-4 text-primary/70" />{badge.label}
                  </span>
                ))}
              </div>
            )}
            {hasDifferentiators && ctx.differentiators.map((item, i) => (
              <motion.div key={i} {...stagger(i)} className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-background/10 flex items-center justify-center shrink-0">
                  <DynamicIcon name={item.icon || 'CheckCircle'} className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1">{item.title}</h3>
                  <p className="text-sm text-background/40 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 3: full-bleed dark band with large icons ──
  if (layout === 'layout_3') return (
    <section className="py-24 lg:py-32 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-background/30 mb-4 block">03 — Why Us</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">Built different</h2>
            <p className="text-background/50 text-lg leading-relaxed">We don't just meet expectations — we redefine them.</p>
            {hasTrustBadges && (
              <div className="flex flex-wrap gap-3 mt-8">
                {trustBadges.map((badge, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-background/10 text-sm text-background/60">
                    <DynamicIcon name={badge.icon || 'Shield'} className="h-4 w-4 text-primary/70" />{badge.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          {hasDifferentiators && (
            <div className="grid sm:grid-cols-2 gap-5">
              {ctx.differentiators.map((item, i) => (
                <motion.div key={i} {...stagger(i)} className="p-5 rounded-xl bg-background/[0.06] border border-background/8">
                  <DynamicIcon name={item.icon || 'CheckCircle'} className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-background/40 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );

  // ── Layout 4: primary-accent feature cards ──
  if (layout === 'layout_4') return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] to-transparent" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="flex items-center gap-4 mb-4"><div className="w-8 h-px bg-primary" /><span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Why Us</span></div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">Built different</h2>
          </div>
        </div>
        {hasTrustBadges && (
          <div className="flex flex-wrap gap-3 mb-12">
            {trustBadges.map((badge, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 bg-foreground text-background rounded-full text-xs font-medium">
                <DynamicIcon name={badge.icon || 'Shield'} className="h-3.5 w-3.5" />{badge.label}
              </span>
            ))}
          </div>
        )}
        {hasDifferentiators && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {ctx.differentiators.map((item, i) => (
              <motion.div key={i} {...stagger(i)} className="p-8 bg-background rounded-2xl border border-border hover:shadow-xl hover:border-primary/20 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <DynamicIcon name={item.icon || 'CheckCircle'} className="h-6 w-6" />
                </div>
                <h3 className="font-heading font-semibold text-base mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );

  // ── Layout 5: brutalist two-column ──
  if (layout === 'layout_5') return (
    <section className="py-24 lg:py-32 border-t-2 border-foreground">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-3">Why Us</span>
            <div className="w-8 h-0.5 bg-primary mb-6" />
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">Built different</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">We don't just meet expectations — we redefine them.</p>
            {hasTrustBadges && (
              <div className="flex flex-wrap gap-2">
                {trustBadges.map((badge, i) => (
                  <span key={i} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                    <DynamicIcon name={badge.icon || 'Shield'} className="h-3.5 w-3.5" />{badge.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          {hasDifferentiators && (
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-foreground/10">
              {ctx.differentiators.map((item, i) => (
                <motion.div key={i} {...stagger(i)} className="p-8 bg-background">
                  <span className="text-5xl font-heading font-black text-foreground/[0.04] block mb-2">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="font-heading font-semibold text-base mb-2 -mt-4 relative z-10">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );

  // ── Layout 1: original ──
  return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <SectionBadge>Why Us</SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Built different</h2>
          <p className="text-muted-foreground text-lg">We don't just meet expectations — we redefine them.</p>
        </div>
        {hasTrustBadges && (<div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">{trustBadges.map((badge, i) => (<motion.div key={i} {...stagger(i)} className="text-center p-8 border border-border rounded-xl"><div className="w-12 h-12 rounded-full bg-foreground/5 flex items-center justify-center mx-auto mb-4"><DynamicIcon name={badge.icon || 'Shield'} className="h-6 w-6 text-foreground/60" /></div><h3 className="font-heading font-semibold text-sm">{badge.label}</h3></motion.div>))}</div>)}
        {hasDifferentiators && (<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">{ctx.differentiators.map((item, i) => (<motion.div key={i} {...stagger(i)} className="flex gap-5"><div className="w-10 h-10 rounded-lg bg-foreground text-background flex items-center justify-center flex-shrink-0"><DynamicIcon name={item.icon || 'CheckCircle'} className="h-5 w-5" /></div><div><h3 className="font-heading font-semibold text-base mb-1">{item.title}</h3><p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p></div></motion.div>))}</div>)}
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// PROCESS
// ══════════════════════════════════════════════════════════════════
const ProcessSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const layout = useLayout();
  if (!ctx.processSteps.length) return null;

  // ── Layout 2: horizontal timeline with connecting line ──
  if (layout === 'layout_2') return (
    <section className="py-20 lg:py-28">
      <div className="container mx-auto px-4 lg:px-8">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Process</p>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-16">How it works</h2>
        <div className="relative">
          <div className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-border" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ctx.processSteps.map((step, i) => (
              <motion.div key={step.step} {...stagger(i)} className="relative">
                <div className="w-16 h-16 rounded-2xl bg-foreground text-background flex items-center justify-center text-lg font-heading font-bold mb-5 relative z-10">{step.step}</div>
                <h3 className="font-heading font-semibold text-base mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 3: oversized step numbers ──
  if (layout === 'layout_3') return (
    <section className="py-24 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4 block">04 — Process</span>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-16">How it works</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {ctx.processSteps.map((step, i) => (
            <motion.div key={step.step} {...stagger(i)} className="relative">
              <span className="text-8xl font-heading font-black text-primary/8 leading-none block mb-2">{String(step.step).padStart(2, '0')}</span>
              <h3 className="font-heading font-semibold text-lg mb-2 -mt-6 relative z-10">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Layout 4: cinematic step cards ──
  if (layout === 'layout_4') return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <div className="flex items-center justify-center gap-4 mb-4"><div className="w-8 h-px bg-primary" /><span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Process</span><div className="w-8 h-px bg-primary" /></div>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">How it works</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ctx.processSteps.map((step, i) => (
            <motion.div key={step.step} {...stagger(i)} className="relative group">
              <div className="p-8 rounded-2xl border border-border bg-background hover:border-primary/30 hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl bg-foreground text-background flex items-center justify-center text-lg font-heading font-bold mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {String(step.step).padStart(2, '0')}
                </div>
                <h3 className="font-heading font-semibold text-base mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
              {i < ctx.processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-border" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Layout 5: brutalist vertical steps ──
  if (layout === 'layout_5') return (
    <section className="py-24 lg:py-32 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <span className="text-[10px] font-mono uppercase tracking-widest text-background/30 block mb-3">Process</span>
            <div className="w-8 h-0.5 bg-primary mb-6" />
            <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">How it works</h2>
          </div>
          <div className="lg:col-span-8 space-y-0">
            {ctx.processSteps.map((step, i) => (
              <motion.div key={step.step} {...stagger(i)} className="flex gap-8 items-start py-10 border-b border-background/10 last:border-0">
                <span className="text-6xl font-heading font-black text-background/[0.08] leading-none shrink-0 w-20 text-right">{String(step.step).padStart(2, '0')}</span>
                <div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-sm text-background/40 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 1: original dark section ──
  return (
    <section className="py-24 lg:py-32 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest text-background/40 border border-background/10 mb-6">Process</span>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">How it works</h2>
          <p className="text-background/50 text-lg">Simple, transparent, from start to finish.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {ctx.processSteps.map((step, i) => (
            <motion.div key={step.step} {...stagger(i)} className="relative text-center">
              <div className="text-6xl font-heading font-bold text-background/8 mb-4">{String(step.step).padStart(2, '0')}</div>
              <h3 className="font-heading font-semibold text-lg mb-2">{step.title}</h3>
              <p className="text-sm text-background/50 leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// TESTIMONIALS
// ══════════════════════════════════════════════════════════════════
const TestimonialsSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { data: googleReviews = [] } = useQuery({
    queryKey: ['google-reviews-homepage'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('google_reviews')
        .select('id, reviewer_name, rating, review_text, reviewer_location')
        .eq('status', 'approved')
        .eq('show_on_homepage', true)
        .gte('rating', 4)
        .order('rating', { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const reviews = googleReviews.length > 0
    ? googleReviews.map(r => ({ name: r.reviewer_name, rating: r.rating, text: r.review_text || '', location: r.reviewer_location || '' }))
    : ctx.testimonials.map(t => ({ name: t.name, rating: t.rating, text: t.text, location: t.location }));

  const layout = useLayout();
  if (!reviews.length) return null;

  // ── Layout 2: single featured review with large quote ──
  if (layout === 'layout_2') return (
    <section className="py-20 lg:py-28 bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-1 mb-6">
            {Array.from({ length: 5 }).map((_, j) => (<Star key={j} className="h-6 w-6 text-amber-400 fill-amber-400" />))}
          </div>
          <blockquote className="font-heading text-2xl lg:text-4xl font-medium leading-relaxed mb-8">
            "{reviews[0].text && reviews[0].text.length > 250 ? reviews[0].text.slice(0, 250) + '...' : reviews[0].text}"
          </blockquote>
          <p className="text-background/50 text-sm mb-8">— {reviews[0].name}{reviews[0].location ? `, ${reviews[0].location}` : ''}</p>
          <Link to="/reviews" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background/15 text-sm font-medium hover:bg-background/5 transition-all">
            Read all {reviews.length > 3 ? `${reviews.length}+ ` : ''}reviews <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );

  // ── Layout 3: large featured review + side cards ──
  if (layout === 'layout_3') return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4 block">05 — Testimonials</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">What people say</h2>
          </div>
          <Link to="/reviews" className="text-sm font-medium text-primary hover:underline flex items-center gap-1">All reviews <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Featured large review */}
          <motion.div {...stagger(0)} className="lg:col-span-3 p-10 bg-foreground text-background rounded-2xl flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-1 mb-6">{Array.from({ length: 5 }).map((_, j) => (<Star key={j} className="h-5 w-5 text-amber-400 fill-amber-400" />))}</div>
              <blockquote className="font-heading text-xl lg:text-2xl font-medium leading-relaxed mb-8">
                "{reviews[0].text && reviews[0].text.length > 300 ? reviews[0].text.slice(0, 300) + '...' : reviews[0].text}"
              </blockquote>
            </div>
            <div className="flex items-center gap-3 pt-6 border-t border-background/10">
              <div className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center text-sm font-bold">{reviews[0].name.charAt(0)}</div>
              <div><p className="font-semibold text-sm">{reviews[0].name}</p>{reviews[0].location && <p className="text-xs text-background/40">{reviews[0].location}</p>}</div>
            </div>
          </motion.div>
          {/* Side reviews */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {reviews.slice(1, 3).map((review, i) => (
              <motion.div key={i} {...stagger(i + 1)} className="p-6 border border-border rounded-xl flex-1 flex flex-col">
                <div className="flex items-center gap-1 mb-4">{Array.from({ length: 5 }).map((_, j) => (<Star key={j} className={cn('h-3.5 w-3.5', j < review.rating ? 'text-foreground fill-foreground' : 'text-border')} />))}</div>
                <p className="text-sm text-foreground leading-relaxed mb-4 flex-1 line-clamp-4">"{review.text}"</p>
                <p className="text-xs font-semibold">{review.name}</p>
                {review.location && <p className="text-xs text-muted-foreground">{review.location}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 4: cinematic large quote spotlight ──
  if (layout === 'layout_4') return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] to-transparent" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="flex items-center gap-4 mb-4"><div className="w-8 h-px bg-primary" /><span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Reviews</span></div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">What people say</h2>
          </div>
          <Link to="/reviews" className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">All reviews <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {/* Large featured review */}
        <motion.div {...stagger(0)} className="bg-foreground text-background rounded-3xl p-10 lg:p-16 mb-6">
          <div className="flex items-center gap-1 mb-8">{Array.from({ length: 5 }).map((_, j) => (<Star key={j} className="h-6 w-6 text-primary fill-primary" />))}</div>
          <blockquote className="font-heading text-2xl lg:text-4xl xl:text-5xl font-medium leading-[1.15] tracking-tight mb-10 max-w-4xl">
            "{reviews[0].text && reviews[0].text.length > 250 ? reviews[0].text.slice(0, 250) + '...' : reviews[0].text}"
          </blockquote>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-background/10 flex items-center justify-center text-base font-bold">{reviews[0].name.charAt(0)}</div>
            <div><p className="font-semibold">{reviews[0].name}</p>{reviews[0].location && <p className="text-sm text-background/40">{reviews[0].location}</p>}</div>
          </div>
        </motion.div>
        {/* Smaller reviews */}
        <div className="grid md:grid-cols-2 gap-6">
          {reviews.slice(1, 3).map((review, i) => (
            <motion.div key={i} {...stagger(i + 1)} className="p-8 rounded-2xl border border-border bg-background">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: 5 }).map((_, j) => (<Star key={j} className={cn('h-4 w-4', j < review.rating ? 'text-foreground fill-foreground' : 'text-border')} />))}</div>
              <p className="text-foreground leading-relaxed mb-5 line-clamp-4">"{review.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-foreground/5 flex items-center justify-center text-xs font-bold">{review.name.charAt(0)}</div>
                <div><p className="text-sm font-semibold">{review.name}</p>{review.location && <p className="text-xs text-muted-foreground">{review.location}</p>}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Layout 5: brutalist single quote ──
  if (layout === 'layout_5') return (
    <section className="py-24 lg:py-32 border-t-2 border-foreground">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-3">Reviews</span>
            <div className="w-8 h-0.5 bg-primary mb-6" />
            <div className="flex items-center gap-1 mb-4">{Array.from({ length: 5 }).map((_, j) => (<Star key={j} className="h-4 w-4 text-foreground fill-foreground" />))}</div>
            <p className="text-sm text-muted-foreground mb-6">Real feedback from real customers.</p>
            <Link to="/reviews" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground border-b-2 border-foreground pb-1 hover:text-primary hover:border-primary transition-colors">All reviews <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="lg:col-span-8">
            {/* Featured large review */}
            <motion.div {...stagger(0)} className="mb-10">
              <blockquote className="font-heading text-2xl lg:text-3xl font-bold leading-[1.2] tracking-tight mb-6">
                "{reviews[0].text && reviews[0].text.length > 300 ? reviews[0].text.slice(0, 300) + '...' : reviews[0].text}"
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-primary" />
                <div><p className="font-semibold text-sm">{reviews[0].name}</p>{reviews[0].location && <p className="text-xs text-muted-foreground">{reviews[0].location}</p>}</div>
              </div>
            </motion.div>
            {/* Secondary reviews */}
            <div className="space-y-0 divide-y-2 divide-foreground/10 border-t-2 border-foreground/10">
              {reviews.slice(1, 3).map((review, i) => (
                <motion.div key={i} {...stagger(i + 1)} className="py-8">
                  <p className="text-foreground leading-relaxed mb-3 line-clamp-3">"{review.text}"</p>
                  <p className="text-xs font-mono text-muted-foreground">{review.name}{review.location ? ` — ${review.location}` : ''}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 1: original ──
  return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl"><SectionBadge><Star className="h-3.5 w-3.5" /> Reviews</SectionBadge><h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">What people say</h2><p className="text-muted-foreground text-lg">Real feedback from real customers.</p></div>
          <Link to="/reviews" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">All reviews <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((review, i) => (
            <motion.div key={i} {...stagger(i)} className="p-8 border border-border rounded-2xl flex flex-col">
              <div className="flex items-center gap-1 mb-6">{Array.from({ length: 5 }).map((_, j) => (<Star key={j} className={cn('h-4 w-4', j < review.rating ? 'text-foreground fill-foreground' : 'text-border')} />))}</div>
              <p className="text-foreground leading-relaxed mb-6 flex-1">"{review.text && review.text.length > 200 ? review.text.slice(0, 200) + '...' : review.text}"</p>
              <div className="pt-6 border-t border-border flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-foreground text-background flex items-center justify-center text-sm font-bold">{review.name.charAt(0).toUpperCase()}</div><div><p className="font-semibold text-sm">{review.name}</p>{review.location && <p className="text-xs text-muted-foreground">{review.location}</p>}</div></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// OFFERS
// ══════════════════════════════════════════════════════════════════
const OffersSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { data: promotions = [] } = useQuery({
    queryKey: ['promotions-homepage'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('promotions')
        .select('id, title, description, discount_type, discount_value, promo_code, is_active')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const formatDiscount = (type: string, value: number) => {
    if (type === 'percentage') return `${value}% OFF`;
    if (type === 'fixed') return `$${value} OFF`;
    return `${value} OFF`;
  };

  const hasDbPromos = promotions.length > 0;
  const hasCtxOffers = ctx.offers.length > 0;
  if (!hasDbPromos && !hasCtxOffers) return null;

  return (
    <section className="py-24 lg:py-32 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <SectionBadge>Special Offers</SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Current promotions</h2>
          <p className="text-muted-foreground text-lg">Take advantage of our limited-time offers.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hasDbPromos ? promotions.map((promo, i) => (
            <motion.div key={promo.id} {...stagger(i)}
              className="border border-border rounded-2xl overflow-hidden bg-background flex flex-col">
              <div className="p-8 flex flex-col flex-1">
                <p className="font-heading text-2xl font-bold text-primary mb-2">
                  {formatDiscount(promo.discount_type, promo.discount_value)}
                </p>
                <h3 className="font-heading font-semibold text-lg mb-2">{promo.title}</h3>
                <p className="text-sm text-muted-foreground mb-6 flex-1">{promo.description}</p>
                <Link to="/contact" className="btn-cta text-sm py-3 justify-center w-full">
                  {promo.promo_code ? `Use Code: ${promo.promo_code}` : 'Claim Offer'}
                </Link>
              </div>
            </motion.div>
          )) : ctx.offers.map((offer, i) => (
            <motion.div key={i} {...stagger(i)}
              className="border border-border rounded-2xl overflow-hidden bg-background flex flex-col">
              <div className="p-8 flex flex-col flex-1">
                <p className="font-heading text-2xl font-bold text-primary mb-2">{offer.discount}</p>
                <h3 className="font-heading font-semibold text-lg mb-2">{offer.title}</h3>
                <p className="text-sm text-muted-foreground mb-6 flex-1">{offer.description}</p>
                <Link to="/contact" className="btn-cta text-sm py-3 justify-center w-full">
                  Get Quote
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/promos" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">
            View all promos <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// ABOUT
// ══════════════════════════════════════════════════════════════════
const AboutSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => (
  <section className="py-24 lg:py-32">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
        <motion.div {...fadeUp}>
          <SectionBadge>About</SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-6">{ctx.aboutTitle}</h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-8">{ctx.aboutDescription}</p>
          <ul className="space-y-4">
            {ctx.aboutHighlights.map((h, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-foreground/40 mt-0.5 flex-shrink-0" />
                <span className="text-[15px]">{h}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Link to="/about" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">
              Learn more about us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
        <motion.div {...fadeUp} transition={{ delay: 0.15 }}>
          {ctx.aboutImageUrl ? (
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img src={ctx.aboutImageUrl} alt="About us" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="rounded-2xl bg-muted aspect-[4/3] flex items-center justify-center">
              <span className="text-muted-foreground/30 text-8xl font-heading font-bold">→</span>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════
// SERVICE AREAS
// ══════════════════════════════════════════════════════════════════
const ServiceAreasSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { data: locations = [] } = useQuery({
    queryKey: ['service-locations-homepage'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('service_locations')
        .select('id, name, slug, region')
        .eq('is_published', true)
        .order('sort_order', { ascending: true })
        .limit(8);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const hasDbLocations = locations.length > 0;
  const areas = hasDbLocations ? [] : ctx.serviceAreas.map(a => typeof a === 'string' ? a : a.name);
  if (!hasDbLocations && !areas.length) return null;

  return (
    <section className="py-24 lg:py-32 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <SectionBadge><MapPin className="h-3.5 w-3.5" /> Locations</SectionBadge>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Where we work</h2>
            <p className="text-muted-foreground text-lg">Proudly serving communities across the region.</p>
          </div>
          <Link to="/service-areas" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">
            All locations <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {hasDbLocations ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {locations.map((loc, i) => (
              <motion.div key={loc.id} {...stagger(i)}>
                <Link
                  to={`/service-areas/${slugify(loc.region || 'area')}/${loc.slug}`}
                  className="group flex items-center gap-4 p-5 border border-border rounded-xl transition-all duration-300 hover:border-foreground/20 bg-background"
                >
                  <div className="w-10 h-10 rounded-full bg-foreground/5 flex items-center justify-center flex-shrink-0 group-hover:bg-foreground group-hover:text-background transition-all">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{loc.name}</p>
                    <p className="text-xs text-muted-foreground">{loc.region}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {areas.map((area) => (
              <Link key={area} to={`/service-areas/${slugify(area)}`}
                className="px-5 py-3 border border-border rounded-full text-sm hover:border-foreground/30 transition-colors">
                {area}
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// FAQ
// ══════════════════════════════════════════════════════════════════
const FAQSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  if (!ctx.faq.length) return null;
  return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
        <div className="text-center mb-16">
          <SectionBadge>FAQ</SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Common questions</h2>
          <p className="text-muted-foreground text-lg">Everything you need to know.</p>
        </div>
        <div className="space-y-0 divide-y divide-border border-t border-b border-border">
          {ctx.faq.map((item, i) => (
            <details key={i} className="group">
              <summary className="flex items-center justify-between py-6 cursor-pointer font-medium text-[15px] hover:text-muted-foreground transition-colors">
                {item.question}
                <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
              </summary>
              <div className="pb-6 text-sm text-muted-foreground leading-relaxed pr-12">{item.answer}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// CONTACT FORM / CTA
// ══════════════════════════════════════════════════════════════════
const ContactFormSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const layout = useLayout();
  const phone = ctx.template.contact?.phone || '';
  const phoneTel = phone.replace(/\D/g, '');

  // ── Layout 2: full-width dark CTA band ──
  if (layout === 'layout_2') return (
    <section className="bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Ready to start?</h2>
            <p className="text-background/50 text-lg mb-8">Get a free, no-obligation estimate. We respond within 24 hours.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-background text-foreground font-semibold hover:bg-background/90 transition-all">Contact Page <ArrowRight size={16} /></Link>
              {phone && <a href={`tel:${phoneTel}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-background/15 font-medium hover:bg-background/5 transition-all"><Phone size={16} />{phone}</a>}
            </div>
          </div>
          <div className="bg-background/[0.06] backdrop-blur-sm border border-background/10 rounded-2xl p-8">
            <LeadForm title="Quick Quote" subtitle="We'll respond within 24 hours." source="homepage_cta" />
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 3: two-tone split CTA ──
  if (layout === 'layout_3') return (
    <section className="bg-muted/30">
      <div className="grid lg:grid-cols-2">
        {/* Left: dark CTA */}
        <div className="bg-foreground text-background px-8 lg:px-16 xl:px-24 py-20 lg:py-24 flex flex-col justify-center">
          <span className="text-[11px] font-mono uppercase tracking-widest text-background/30 mb-4 block">Get Started</span>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">Ready to talk?</h2>
          <p className="text-background/50 text-lg mb-8 leading-relaxed">Get a free, no-obligation estimate. We respond within 24 hours.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            {phone && <a href={`tel:${phoneTel}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-background text-foreground font-semibold hover:bg-background/90 transition-all"><Phone size={18} /> Call {phone}</a>}
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-background/15 font-medium hover:bg-background/5 transition-all">Contact Page <ArrowRight size={16} /></Link>
          </div>
        </div>
        {/* Right: form */}
        <div className="px-8 lg:px-16 xl:px-24 py-20 lg:py-24 flex items-center bg-background">
          <div className="w-full max-w-md mx-auto">
            <LeadForm title="Quick Quote" subtitle="We'll respond within 24 hours." source="homepage_cta" />
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 4: cinematic full-bleed CTA ──
  if (layout === 'layout_4') return (
    <section className="relative py-24 lg:py-32 overflow-hidden bg-foreground text-background">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-foreground to-foreground" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
      }} />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-4 mb-6"><div className="w-8 h-px bg-primary" /><span className="text-xs uppercase tracking-[0.3em] text-background/30">Get Started</span></div>
            <h2 className="font-heading text-4xl lg:text-6xl font-bold tracking-tight mb-6">Ready to start?</h2>
            <p className="text-xl text-background/40 mb-10 leading-relaxed">Get a free, no-obligation estimate. We respond within the hour.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              {phone && <a href={`tel:${phoneTel}`} className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-primary text-primary-foreground rounded-full text-base font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/25"><Phone size={18} /> Call {phone}</a>}
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full border border-background/15 font-medium hover:bg-background/5 transition-all backdrop-blur-sm">Contact Page <ArrowRight size={16} /></Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 blur-2xl" />
            <div className="relative bg-background/[0.06] backdrop-blur-2xl border border-background/10 rounded-3xl p-10">
              <LeadForm title="Quick Quote" subtitle="We'll respond within the hour." source="homepage_cta_l4" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 5: brutalist split CTA ──
  if (layout === 'layout_5') return (
    <section className="border-t-2 border-foreground">
      <div className="grid lg:grid-cols-2">
        <div className="px-8 lg:px-16 xl:px-24 py-20 lg:py-28 flex flex-col justify-center">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-3">Contact</span>
          <div className="w-8 h-0.5 bg-primary mb-6" />
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-4">Ready to talk?</h2>
          <p className="text-muted-foreground leading-relaxed mb-8">Get a free, no-obligation estimate.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            {phone && <a href={`tel:${phoneTel}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-foreground text-background rounded-none text-sm font-semibold uppercase tracking-wider hover:bg-foreground/90 transition-all"><Phone size={16} /> {phone}</a>}
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-foreground rounded-none text-sm font-semibold uppercase tracking-wider hover:bg-foreground hover:text-background transition-all">Contact Page <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="bg-foreground text-background px-8 lg:px-16 xl:px-24 py-20 lg:py-28 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <LeadForm title="Request a Quote" subtitle="Free estimate — no obligation." source="homepage_cta_l5" />
          </div>
        </div>
      </div>
    </section>
  );

  // ── Layout 1: original ──
  return (
    <section className="py-24 lg:py-32 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          <div>
            <SectionBadge>Contact</SectionBadge>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Let's talk</h2>
            <p className="text-muted-foreground text-lg mb-8">Ready for a free consultation? Fill out the form and we'll get back to you within 24 hours.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-cta text-sm px-6 py-3">Contact Page <ArrowRight size={14} /></Link>
              {phone && <a href={`tel:${phoneTel}`} className="btn-secondary text-sm px-6 py-3"><Phone size={14} />Call {phone}</a>}
            </div>
          </div>
          <div className="bg-background border border-border rounded-2xl p-8 shadow-sm">
            <LeadForm title="Quick Quote" subtitle="We'll respond within 24 hours." source="homepage_cta" />
          </div>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// FEATURED PROJECTS
// ══════════════════════════════════════════════════════════════════
const FeaturedProjectsSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { data: projects = [] } = useQuery({
    queryKey: ['featured-projects-public'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('featured_projects')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  if (!projects.length) return null;

  return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <SectionBadge>Portfolio</SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">{ctx.featuredProjectsTitle}</h2>
          <p className="text-muted-foreground text-lg">{ctx.featuredProjectsSubtitle}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project: any, i: number) => (
            <motion.div key={project.id} {...stagger(i)}
              className="border border-border rounded-2xl overflow-hidden group hover:shadow-lg transition-shadow bg-background">
              <div className="aspect-video relative overflow-hidden bg-muted">
                {project.media_type === 'video' ? (
                  <video
                    src={project.media_url}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    muted loop playsInline
                    onMouseOver={(e) => (e.target as HTMLVideoElement).play()}
                    onMouseOut={(e) => (e.target as HTMLVideoElement).pause()}
                  />
                ) : (
                  <img
                    src={project.media_url}
                    alt={project.title || 'Project'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                )}
              </div>
              {(project.title || project.description) && (
                <div className="p-5">
                  {project.title && <h3 className="font-heading font-semibold text-base mb-1">{project.title}</h3>}
                  {project.description && <p className="text-sm text-muted-foreground">{project.description}</p>}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// MAINTENANCE PLANS
// ══════════════════════════════════════════════════════════════════
const MaintenancePlansSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { data: plans = [] } = useQuery({
    queryKey: ['maintenance-plans-homepage'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('maintenance_plans')
        .select('id, name, slug, tagline, price, billing_period, features, highlights, is_popular, cta_text, cta_link, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(4);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  if (!plans.length) return null;

  return (
    <section className="py-24 lg:py-32 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <SectionBadge>Plans & Pricing</SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Maintenance plans</h2>
          <p className="text-muted-foreground text-lg">Keep everything running smoothly with a plan that fits your needs.</p>
        </div>

        <div className={cn(
          'grid gap-6 max-w-5xl mx-auto',
          plans.length === 1 ? 'md:grid-cols-1 max-w-md' :
          plans.length === 2 ? 'md:grid-cols-2 max-w-3xl' :
          plans.length >= 3 ? 'md:grid-cols-3' : ''
        )}>
          {plans.map((plan: any, i: number) => (
            <motion.div key={plan.id} {...stagger(i)} className="relative">
              <div className={cn(
                'relative h-full flex flex-col p-8 rounded-xl border transition-all duration-300',
                plan.is_popular
                  ? 'border-foreground shadow-lg scale-[1.02] z-10 bg-background'
                  : 'border-border bg-background hover:border-foreground/20 hover:shadow-md'
              )}>
                {plan.is_popular && (
                  <div className="absolute -top-3 left-6">
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-foreground text-background rounded-full">
                      <Star className="h-3 w-3" /> Most Popular
                    </span>
                  </div>
                )}

                <div className={plan.is_popular ? 'pt-2' : ''}>
                  <h3 className="font-heading font-bold text-xl mb-1">{plan.name}</h3>
                  {plan.tagline && <p className="text-sm text-muted-foreground mb-4">{plan.tagline}</p>}

                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-foreground">${plan.price}</span>
                    <span className="text-muted-foreground text-sm">/{plan.billing_period}</span>
                  </div>
                </div>

                {plan.features && plan.features.length > 0 && (
                  <ul className="space-y-2.5 flex-1 mb-8">
                    {(plan.features as string[]).slice(0, 6).map((f: string, fi: number) => (
                      <li key={fi} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{f}</span>
                      </li>
                    ))}
                    {(plan.features as string[]).length > 6 && (
                      <li className="text-xs text-primary font-medium">+{(plan.features as string[]).length - 6} more features</li>
                    )}
                  </ul>
                )}

                <Link
                  to={plan.cta_link || '/contact'}
                  className={cn(
                    'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-colors w-full',
                    plan.is_popular
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-foreground text-background hover:bg-foreground/90'
                  )}
                >
                  {plan.cta_text || 'Get Started'}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/maintenance-plans" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">
            View all plan details <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// BRANDS — Featured Manufacturer Showcase with FAQ Schema
// ══════════════════════════════════════════════════════════════════
const BrandsSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const [expandedBrand, setExpandedBrand] = React.useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = React.useState<string | null>(null);

  const { data: brands = [] } = useQuery({
    queryKey: ['brands-homepage-featured'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('brands')
        .select('*')
        .eq('is_active', true)
        .eq('is_featured', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  if (!brands.length) return null;

  // Build FAQs from brand attributes for schema
  const brandFaqs = brands.flatMap((brand: any) => {
    const faqs: { question: string; answer: string }[] = [];
    if (brand.short_description) {
      faqs.push({
        question: `Why should I choose ${brand.name} products?`,
        answer: brand.short_description,
      });
    }
    if (brand.description) {
      faqs.push({
        question: `What makes ${brand.name} different from other brands?`,
        answer: brand.description.slice(0, 300) + (brand.description.length > 300 ? '...' : ''),
      });
    }
    const attrs = Array.isArray(brand.attributes) ? brand.attributes : [];
    const keyProducts = attrs.find((a: any) => a.key === 'Key Products');
    if (keyProducts) {
      faqs.push({
        question: `What ${brand.name} products do you offer?`,
        answer: `We carry a full range of ${brand.name} products including ${keyProducts.value}. Contact us for availability and pricing.`,
      });
    }
    return faqs;
  });

  // FAQPage JSON-LD schema
  const faqSchema = brandFaqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: brandFaqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  } : null;

  const getAttributes = (brand: any) => {
    if (!Array.isArray(brand.attributes)) return [];
    return brand.attributes as { key: string; value: string }[];
  };

  return (
    <section className="py-24 lg:py-32 bg-muted/30">
      {/* FAQ Schema */}
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <SectionBadge>
            <Award className="h-3.5 w-3.5" /> Brands We Trust
          </SectionBadge>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">
            Premium Brands, Proven Performance
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            We partner with industry-leading manufacturers to deliver the highest quality products backed by innovation, efficiency, and reliability.
          </p>
        </div>

        {/* Brand Logo Strip */}
        <div className="flex flex-wrap justify-center items-center gap-8 mb-16 pb-16 border-b border-border">
          {brands.map((brand: any, i: number) => (
            <motion.button
              key={brand.id}
              {...stagger(i)}
              onClick={() => setExpandedBrand(expandedBrand === brand.id ? null : brand.id)}
              className={cn(
                'relative p-4 rounded-xl transition-all duration-300 cursor-pointer',
                expandedBrand === brand.id
                  ? 'bg-background shadow-lg ring-2 ring-primary/20 scale-110'
                  : 'hover:bg-background/60 hover:shadow-md'
              )}
            >
              {brand.logo_url ? (
                <img
                  src={brand.logo_url}
                  alt={`${brand.name} - Authorized Dealer`}
                  className={cn(
                    'h-12 w-auto max-w-[140px] object-contain transition-all duration-300',
                    expandedBrand === brand.id ? 'opacity-100 grayscale-0' : 'opacity-50 grayscale hover:opacity-80'
                  )}
                  loading="lazy"
                />
              ) : (
                <span className={cn(
                  'text-base font-semibold transition-colors',
                  expandedBrand === brand.id ? 'text-foreground' : 'text-muted-foreground/50'
                )}>{brand.name}</span>
              )}
            </motion.button>
          ))}
        </div>

        {/* Expanded Brand Detail */}
        {brands.map((brand: any) => {
          if (expandedBrand !== brand.id) return null;
          const attrs = getAttributes(brand);
          const keyProducts = attrs.find(a => a.key === 'Key Products')?.value;
          const brandValues = attrs.find(a => a.key === 'Brand Values')?.value;

          return (
            <motion.div
              key={brand.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="mb-16"
            >
              <div className="bg-background rounded-2xl border border-border p-8 lg:p-12 shadow-sm">
                <div className="grid lg:grid-cols-3 gap-10">
                  {/* Brand Info */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-4 mb-6">
                      {brand.logo_url && (
                        <img src={brand.logo_url} alt={brand.name} className="h-12 w-auto object-contain" />
                      )}
                      <div>
                        <h3 className="font-heading text-2xl font-bold">{brand.name}</h3>
                        {brand.short_description && (
                          <p className="text-muted-foreground text-sm mt-1">{brand.short_description}</p>
                        )}
                      </div>
                    </div>

                    {brand.description && (
                      <p className="text-muted-foreground leading-relaxed mb-8 text-[15px]">
                        {brand.description.slice(0, 500)}{brand.description.length > 500 ? '...' : ''}
                      </p>
                    )}

                    {/* Key Products */}
                    {keyProducts && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Products We Carry</h4>
                        <div className="flex flex-wrap gap-2">
                          {keyProducts.split(',').map((product: string, i: number) => (
                            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/5 text-foreground text-sm rounded-lg border border-primary/10">
                              <CheckCircle className="h-3.5 w-3.5 text-primary" />
                              {product.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Brand Values */}
                    {brandValues && (
                      <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Why {brand.name}?</h4>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {brandValues.split(',').map((value: string, i: number) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Shield className="h-4 w-4 text-primary" />
                              </div>
                              <span className="text-sm font-medium">{value.trim()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick Facts Sidebar */}
                  <div className="space-y-4">
                    <div className="bg-muted/50 rounded-xl p-6 space-y-4">
                      <h4 className="font-heading font-semibold text-base">Quick Facts</h4>
                      {attrs.filter(a => !['Key Products', 'Brand Values'].includes(a.key)).map((attr, i) => (
                        <div key={i} className="flex justify-between items-start gap-2 text-sm py-2 border-b border-border/50 last:border-0">
                          <span className="text-muted-foreground">{attr.key}</span>
                          <span className="font-medium text-right max-w-[60%]">{attr.value}</span>
                        </div>
                      ))}
                    </div>

                    <Link
                      to={`/brands/${brand.slug}`}
                      className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-foreground text-background rounded-xl text-sm font-semibold hover:bg-foreground/90 transition-colors"
                    >
                      Explore {brand.name} Products <ArrowRight className="h-4 w-4" />
                    </Link>

                    {brand.website_url && (
                      <a
                        href={brand.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 border border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                      >
                        Visit {brand.name} Website
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}

        {/* Brand FAQs with Schema */}
        {brandFaqs.length > 0 && (
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mb-3">
                Frequently Asked Questions
              </h3>
              <p className="text-muted-foreground">
                Common questions about the brands we partner with
              </p>
            </div>
            <div className="space-y-3">
              {brandFaqs.map((faq, i) => {
                const faqId = `brand-faq-${i}`;
                const isOpen = expandedFaq === faqId;
                return (
                  <motion.div
                    key={faqId}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    className="border border-border rounded-xl overflow-hidden bg-background"
                  >
                    <button
                      onClick={() => setExpandedFaq(isOpen ? null : faqId)}
                      className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-muted/30 transition-colors"
                    >
                      <span className="font-medium text-[15px]">{faq.question}</span>
                      <ChevronRight className={cn(
                        'h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-200',
                        isOpen && 'rotate-90'
                      )} />
                    </button>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        className="px-5 pb-5"
                      >
                        <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-16">
          <Link
            to="/brands"
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1"
          >
            View all brands we carry <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// JOBS — Open Positions
// ══════════════════════════════════════════════════════════════════
const JobsSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs-homepage'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('jobs')
        .select('id, title, slug, department, location, employment_type, is_published')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(4);
      if (error) return [];
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  if (!jobs.length) return null;

  return (
    <section className="py-24 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <SectionBadge>Careers</SectionBadge>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Join our team</h2>
            <p className="text-muted-foreground text-lg">We're growing and looking for talented people.</p>
          </div>
          <Link to="/careers" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">
            View all openings <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {jobs.map((job: any, i: number) => (
            <motion.div key={job.id} {...stagger(i)}>
              <Link
                to={`/careers/${job.slug}`}
                className="group flex items-center gap-4 p-6 border border-border rounded-xl hover:border-foreground/20 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0 group-hover:bg-foreground group-hover:text-background transition-all">
                  <Briefcase className="h-5 w-5 text-foreground/50 group-hover:text-background transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-base mb-0.5 truncate">{job.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {[job.department, job.location, job.employment_type].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all flex-shrink-0" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// TEAM — Staff Directory
// ══════════════════════════════════════════════════════════════════
const TeamSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { data: members = [] } = useQuery({
    queryKey: ['team-homepage'],
    queryFn: async () => {
      const res: any = await pmsFrom('team_members' as any)
        .select('id, full_name, title, department, featured_image, is_published')
        .eq('is_published', true)
        .order('sort_order', { ascending: true })
        .limit(6);
      if (res.error) return [];
      return res.data || [];
    },
    staleTime: 1000 * 60 * 5,
  });

  if (!members.length) return null;

  return (
    <section className="py-24 lg:py-32 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <SectionBadge>Our Team</SectionBadge>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight mb-4">Meet the experts</h2>
            <p className="text-muted-foreground text-lg">The people behind every successful project.</p>
          </div>
          <Link to="/team" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all border-b border-foreground pb-1">
            View full team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className={cn(
          'grid gap-6',
          members.length <= 3 ? 'md:grid-cols-3 max-w-4xl mx-auto' :
          'md:grid-cols-2 lg:grid-cols-3'
        )}>
          {members.map((member: any, i: number) => (
            <motion.div key={member.id} {...stagger(i)}
              className="text-center p-8 border border-border rounded-xl bg-background">
              <div className="w-20 h-20 rounded-full bg-foreground/5 mx-auto mb-4 overflow-hidden">
                {member.featured_image ? (
                  <img src={member.featured_image} alt={member.full_name || ''} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-heading font-bold text-foreground/20">
                    {member.full_name?.[0]}
                  </div>
                )}
              </div>
              <h3 className="font-heading font-semibold text-base">{member.full_name}</h3>
              <p className="text-sm text-muted-foreground">{member.title}</p>
              {member.department && <p className="text-xs text-muted-foreground/60 mt-1">{member.department}</p>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════
// SECTION MAP & MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════

// Adaptive Hero — picks Custom mode, or one of Layout 1/2/3 based on page design config
const AdaptiveHeroSection: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = ({ ctx }) => {
  const { isCustomMode } = useContentMode();

  // Direct query for homepage layout — bypasses any caching ambiguity
  const { data: layoutRow, isLoading } = useQuery({
    queryKey: ['homepage-layout'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('page_design_config')
        .select('hero_style')
        .eq('page_type', 'homepage')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const layout = (layoutRow?.hero_style || 'layout_1').trim();

  if (isCustomMode) return <CustomHero />;

  // Don't flash the default layout while the real config is still loading
  if (isLoading) {
    return (
      <section className="min-h-[80vh] flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </section>
    );
  }

  switch (layout) {
    case 'layout_2': return <HeroLayout2 ctx={ctx} />;
    case 'layout_3': return <HeroLayout3 ctx={ctx} />;
    case 'layout_4': return <HeroLayout4 ctx={ctx} />;
    case 'layout_5': return <HeroLayout5 ctx={ctx} />;
    default:         return <HeroSection ctx={ctx} />;
  }
};

// Shop section wrapper to match the section component signature
const ShopSectionWrapper: React.FC<{ ctx: ReturnType<typeof useSiteContent> }> = () => {
  const { ShopSection } = React.useMemo(() => ({ ShopSection: React.lazy(() => import('@/components/common/ShopSection').then(m => ({ default: m.ShopSection }))) }), []);
  return (
    <React.Suspense fallback={<div className="py-20 text-center text-muted-foreground">Loading shop...</div>}>
      <ShopSection />
    </React.Suspense>
  );
};

const SECTION_COMPONENTS: Record<string, React.FC<{ ctx: ReturnType<typeof useSiteContent> }>> = {
  hero: AdaptiveHeroSection,
  segments: SegmentsSection,
  services: ServicesSection,
  why_choose_us: WhyChooseUsSection,
  process: ProcessSection,
  testimonials: TestimonialsSection,
  offers: OffersSection,
  about: AboutSection,
  service_areas: ServiceAreasSection,
  faq: FAQSection,
  contact_form: ContactFormSection,
  featured_projects: FeaturedProjectsSection,
  brands: BrandsSection,
  maintenance_plans: MaintenancePlansSection,
  jobs: JobsSection,
  team: TeamSection,
  shop: ShopSectionWrapper,
};

const VISIBILITY_KEY_MAP: Record<string, keyof SectionVisibility> = {
  hero: 'show_hero',
  segments: 'show_segments',
  services: 'show_services',
  why_choose_us: 'show_why_choose_us',
  process: 'show_process',
  testimonials: 'show_testimonials',
  offers: 'show_offers',
  about: 'show_about',
  service_areas: 'show_service_areas',
  faq: 'show_faq',
  contact_form: 'show_contact_form',
  featured_projects: 'show_featured_projects',
  brands: 'show_brands',
  maintenance_plans: 'show_maintenance_plans',
  jobs: 'show_jobs',
  team: 'show_team',
  shop: 'show_shop',
};

export const HomePage: React.FC = () => {
  const ctx = useSiteContent();
  const { visibility, sectionOrder } = useSectionVisibility();
  const { showBreadcrumbs, showTrustBadges, showCtaButtons, showFaqSection, showLeadForm } = usePageDesign('homepage');

  // Read the active homepage layout so all sections can style accordingly
  const { data: layoutRow } = useQuery({
    queryKey: ['homepage-layout'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('page_design_config')
        .select('hero_style')
        .eq('page_type', 'homepage')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
  const activeLayout = ((layoutRow?.hero_style || 'layout_1').trim()) as LayoutId;

  if (ctx.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <LayoutCtx.Provider value={activeLayout}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        {sectionOrder.map((sectionId) => {
          const visKey = VISIBILITY_KEY_MAP[sectionId];
          if (visKey && !visibility[visKey]) return null;

          const Component = SECTION_COMPONENTS[sectionId];
          if (!Component) return null;

          return <Component key={sectionId} ctx={ctx} />;
        })}

        {/* Mobile Lead Form */}
        <section className="py-12 lg:hidden border-t border-border">
          <div className="container mx-auto px-4">
            <LeadForm title="Get Your Free Quote" subtitle="Fill out the form and we'll respond within 24 hours." showPromoField source="homepage_mobile" />
          </div>
        </section>
      </motion.div>
    </LayoutCtx.Provider>
  );
};
